// ═══════════════════════════════════════════════════════════════════════════
//  src/contacto.js
//  EL WHATSAPP DE GERENCIA, Y CÓMO SE ESCRIBE UN MENSAJE PARA QUE LLEGUE BIEN
//
//  Rob: «todo tiene que llegar a mí: si un teacher o un alumno suspende o mueve
//  la clase, todo tiene que pasar por WhatsApp a mí».
//
//  El número está escrito a mano en una decena de pantallas más (login, planes,
//  suscripción…). Las pantallas de cambios de clase lo leen de aquí; el día que
//  cambie el número, esas no se quedan con el viejo.
//
//  Gemelo en el servidor: `backend-api/src/utils/whatsapp.py`.
// ═══════════════════════════════════════════════════════════════════════════

export const WHATSAPP_GERENCIA = '5215610480746';

// ── LO QUE WHATSAPP PINTA COMO UN ROMBO CON INTERROGACIÓN (U+FFFD) ───────────
// Rob, 11-09-2026, con el horario que exporta el CRM delante: «tenemos un emoji o
// un elemento que no lo detecta bien, vamos a quitarlo de todos los mensajes que
// enviamos por WhatsApp».
//
// Lo medido en su mensaje: «—», «•» y «→» llegaban bien, y justo donde iban el
// calendario y la profesora llegaba el rombo roto. Los que se rompían son emojis
// de fuera del plano básico de Unicode (cuatro bytes); los que llegaban bien,
// símbolos del plano básico.
//
// Se quitan TODOS los emojis, también los del plano básico (check verde, aviso,
// pausa…): no hay forma de comprobar desde aquí cómo los pinta cada teléfono, y
// Rob pidió quitarlos de todos. Se conservan las negritas `*texto*`, las cursivas
// `_texto_`, los saltos de línea y la tipografía que sí llega: « — • → · ¡ ¿ ».
//
// ── LOS RANGOS SE ESCRIBEN CON NÚMEROS, NO CON LOS CARACTERES ────────────────
// La primera versión llevaba los caracteres pegados tal cual en el código, y entre
// ellos iban cuatro INVISIBLES (U+200D, U+FE0E, U+FE0F, U+20E3). Un editor que
// «limpia» espacios raros se los podía llevar sin que nadie lo viera, y el
// limpiador dejaría de limpiar en silencio. Con números no hay nada que borrar.
//
// Son los MISMOS rangos que en el gemelo del servidor, para que un mensaje salga
// igual lo arme quien lo arme.
const cp = (n) => String.fromCodePoint(n);
const rango = (a, b) => `${cp(a)}-${cp(b)}`;
const CLASE = `[${[
  rango(0x10000, 0x10FFFF),   // fuera del plano básico: casi todos los emojis
  rango(0x2300, 0x23FF),      // relojes, pausa, reloj de arena
  rango(0x2600, 0x27BF),      // sol, check, cruz, lápiz
  rango(0x2B00, 0x2BFF),      // estrella
  cp(0x2139),                 // «i» de información
  cp(0x200D), cp(0xFE0E), cp(0xFE0F), cp(0x20E3),   // pegamentos invisibles
].join('')}]`;

// Un emoji al principio de una línea, o después de un espacio, se lleva UN espacio
// detrás: «[calendario] *Clase:*» queda «*Clase:*», no « *Clase:*». Uno pegado a
// otra cosa —el «1» de un número con tecla— se quita solo él, porque el espacio
// siguiente es de la palabra que viene.
const EMOJI_Y_ESPACIO = new RegExp(`(^|\\s)${CLASE}+ `, 'gmu');
const EMOJI = new RegExp(`${CLASE}+`, 'gu');

/** El texto sin nada que WhatsApp pueda pintar roto, con sus negritas y saltos. */
export function limpiaParaWhatsApp(texto) {
  let s = String(texto ?? '');
  // Se repite hasta que no cambia: dos emojis seguidos con espacio entre ellos
  // dejarían, en una sola pasada, un espacio suelto al principio de la línea.
  for (let previo = null; previo !== s;) {
    previo = s;
    s = s.replace(EMOJI_Y_ESPACIO, '$1');
  }
  return s
    .replace(EMOJI, '')
    .split('\n')
    .map((linea) => linea.replace(/[ \t]+$/, ''))
    .join('\n');
}

/**
 * El enlace que abre WhatsApp con el mensaje ya escrito y ya limpio.
 * Sin número (`numero = ''`) abre WhatsApp para elegir a quién mandarlo.
 */
export function enlaceWhatsApp(texto, numero = WHATSAPP_GERENCIA) {
  const base = numero ? `https://wa.me/${numero}` : 'https://wa.me/';
  return `${base}?text=${encodeURIComponent(limpiaParaWhatsApp(texto))}`;
}
